<script setup lang="ts">
import { computed, onMounted, ref, watch } from 'vue';
import { useStore } from 'vuex';
import { useRoute, useRouter } from 'vue-router';
import { useAbility } from '@casl/vue';
import Button from 'primevue/button';
import Select from 'primevue/select';
import ProgressSpinner from 'primevue/progressspinner';
import {
  getAccountContainerTypes,
  getAccountFindContainers,
  getAccountGeofences,
} from '@gpx-spa/queries';
import type {
  FindContainersParams,
  FindContainersSite,
} from '@gpx-spa/queries/src/getAccountFindContainers';
import { unitShortLabel } from '@gpx-spa/ui';
import UIEmptyState from '@/components/ui/UIEmptyState.vue';
import FindContainersList from '@/components/find-containers/FindContainersList.vue';
import FindContainersMap from '@/components/find-containers/FindContainersMap.vue';
import {
  FIND_CONTAINERS_DISTANCE_UNIT,
  filtersFromQuery,
  filtersToQuery,
  findContainersEmptyState,
  radiusToMeters,
  sortSites,
  type FindContainersSort,
} from '@/utils/find-containers';
import {
  containerSiteSelectedPayload,
  containersSearchedPayload,
  trackFindContainersOpened,
} from '@/utils/find-containers-tracking';
import * as events from '@/events';
import { setTitle } from '@/utils/title';
import {
  PVT_SPINNER_STROKE_WIDTH,
  PVT_SPINNER_STYLE_MEDIUM,
} from '@/constants/progress-spinner';

type ContainerType = { id: number; name: string; device_count?: number };
type GeofenceOption = { id: number; name: string; lat?: number; lng?: number };

const RADIUS_CHOICES = [50, 150, 500];
const SORT_OPTIONS: { label: string; value: FindContainersSort }[] = [
  { label: 'Distance', value: 'distance' },
  { label: 'Most on site', value: 'units' },
];

setTitle('Find Containers');
onMounted(trackFindContainersOpened);

const store = useStore();
const route = useRoute();
const router = useRouter();
const { can } = useAbility();
const accountId = computed(() => store.getters['auth/accountId'] as number);
const unit = FIND_CONTAINERS_DISTANCE_UNIT;
const unitLabel = unitShortLabel(unit);
const FIELD_LABEL_CLASS =
  'text-xs font-medium uppercase tracking-wide text-gpx_gray-100';
const radiusOptions = [
  ...RADIUS_CHOICES.map((value) => ({ label: `${value} ${unitLabel}`, value })),
  { label: 'Anywhere', value: null },
];

const { data: containerTypesData, isLoading: containerTypesLoading } =
  getAccountContainerTypes(accountId, { per_page: 1000 });
const { data: geofencesData } = getAccountGeofences(accountId);

const containerTypes = computed<ContainerType[]>(
  () => containerTypesData.value?.data ?? [],
);
const destinations = computed<GeofenceOption[]>(() =>
  [...((geofencesData.value ?? []) as GeofenceOption[])].sort((a, b) =>
    a.name.localeCompare(b.name),
  ),
);

const initialFilters = filtersFromQuery(route.query, RADIUS_CHOICES);
const containerTypeId = ref(initialFilters.containerTypeId);
const destinationId = ref(initialFilters.destinationId);
const radius = ref(initialFilters.radius);
const sort = ref(initialFilters.sort);
const radiusMeters = computed(() => radiusToMeters(radius.value, unit));

watch(
  containerTypes,
  (types) => {
    if (containerTypeId.value == null && types.length === 1) {
      containerTypeId.value = types[0].id;
    }
  },
  { immediate: true },
);

watch([containerTypeId, destinationId, radius, sort], () => {
  void router.replace({
    query: filtersToQuery({
      containerTypeId: containerTypeId.value,
      destinationId: destinationId.value,
      radius: radius.value,
      sort: sort.value,
    }),
  });
});

const params = computed<FindContainersParams>(() => ({
  container_type_id: containerTypeId.value,
  destination_geofence_id: destinationId.value,
  radius_m: radiusMeters.value,
}));

const {
  data: result,
  isFetching,
  isError,
} = getAccountFindContainers(accountId, params);

const hasSelection = computed(
  () => containerTypeId.value != null && destinationId.value != null,
);

let trackedSearch: string | null = null;
watch([params, result, isFetching], () => {
  const typeId = containerTypeId.value;
  const destId = destinationId.value;
  if (typeId == null || destId == null) return;
  if (isFetching.value || !result.value) return;
  const search = JSON.stringify(params.value);
  if (search === trackedSearch) return;
  trackedSearch = search;
  events.trigger(
    events.names.CONTAINERS_SEARCHED,
    containersSearchedPayload({
      containerTypeId: typeId,
      destinationGeofenceId: destId,
      radiusMiles: radius.value,
      radiusMeters: radiusMeters.value,
      result: result.value,
    }),
  );
});

const sites = computed(
  () => (hasSelection.value ? result.value?.sites : null) ?? [],
);

const sortedSites = computed(() => sortSites(sites.value, sort.value));

const trackers = computed(
  () => (hasSelection.value ? result.value?.trackers : null) ?? [],
);

const selectedType = computed(() =>
  containerTypes.value.find((t) => t.id === containerTypeId.value),
);

const destination = computed(() => {
  const g = destinations.value.find((d) => d.id === destinationId.value);
  if (!g || g.lat == null || g.lng == null) return null;
  return { lat: Number(g.lat), lng: Number(g.lng), name: g.name };
});

const emptyState = computed(() => {
  if (containerTypesLoading.value) return null;
  return findContainersEmptyState({
    containerTypeCount: containerTypes.value.length,
    typeTrackerCount: selectedType.value?.device_count ?? null,
    result: hasSelection.value && !isFetching.value ? result.value : null,
    radiusMeters: radiusMeters.value,
  });
});

const selectedGeofenceId = ref<number | null>(null);
const map = ref<InstanceType<typeof FindContainersMap> | null>(null);

watch(params, () => {
  selectedGeofenceId.value = null;
});

function trackAddContainerTypesClick() {
  events.trigger(events.names.ADD_CONTAINER_TYPES_CLICKED);
}

function selectSite(site: FindContainersSite, from: 'list' | 'map') {
  events.trigger(
    events.names.CONTAINER_SITE_SELECTED,
    containerSiteSelectedPayload(site, sortedSites.value, from),
  );
  selectedGeofenceId.value = site.geofence_id;
  map.value?.fitToSite(site);
}

function resetMap() {
  selectedGeofenceId.value = null;
  map.value?.fitToContent();
}
</script>

<template>
  <div
    class="h-layout-mobile sm:h-layout-tablet md:h-layout-desktop flex flex-col bg-gpx_gray-900"
  >
    <h1 class="shrink-0 px-6 pb-4 pt-5 text-gpx_lg font-semibold text-gray-900">
      Find Containers
    </h1>

    <div
      class="mx-6 mb-4 grid shrink-0 grid-cols-1 gap-3 rounded-xl border border-gpx_gray-800 bg-white px-5 py-4 shadow-sm sm:grid-cols-[2fr_2fr_1fr_1fr_auto] sm:items-end"
    >
      <div class="flex min-w-0 flex-col gap-1.5">
        <span id="find-containers-type-label" :class="FIELD_LABEL_CLASS">
          Container type
        </span>
        <Select
          v-model="containerTypeId"
          :options="containerTypes"
          option-label="name"
          option-value="id"
          placeholder="Choose a type"
          class="w-full"
          aria-labelledby="find-containers-type-label"
          :loading="containerTypesLoading"
          filter
          auto-filter-focus
        />
      </div>
      <div class="flex min-w-0 flex-col gap-1.5">
        <span id="find-containers-destination-label" :class="FIELD_LABEL_CLASS">
          Destination
        </span>
        <Select
          v-model="destinationId"
          :options="destinations"
          option-label="name"
          option-value="id"
          placeholder="Choose a location"
          class="w-full"
          aria-labelledby="find-containers-destination-label"
          filter
          auto-filter-focus
        />
      </div>
      <div class="flex min-w-0 flex-col gap-1.5">
        <span id="find-containers-radius-label" :class="FIELD_LABEL_CLASS">
          Radius
        </span>
        <Select
          v-model="radius"
          :options="radiusOptions"
          option-label="label"
          option-value="value"
          placeholder="Anywhere"
          class="w-full"
          aria-labelledby="find-containers-radius-label"
        />
      </div>
      <div class="flex min-w-0 flex-col gap-1.5">
        <span id="find-containers-sort-label" :class="FIELD_LABEL_CLASS">
          Sort by
        </span>
        <Select
          v-model="sort"
          :options="SORT_OPTIONS"
          option-label="label"
          option-value="value"
          class="w-full"
          aria-labelledby="find-containers-sort-label"
        />
      </div>
      <Button
        label="Reset map"
        icon="pi pi-refresh"
        severity="secondary"
        outlined
        :disabled="!hasSelection"
        @click="resetMap"
      />
    </div>

    <div
      class="flex min-h-0 flex-1 flex-col gap-4 px-6 pb-6 md:grid md:grid-cols-2"
    >
      <div class="flex min-h-0 flex-col gap-4">
        <section
          class="relative max-h-[40vh] min-h-0 overflow-y-auto rounded-xl border border-gpx_gray-800 bg-white shadow-sm md:max-h-none md:flex-1"
          aria-label="Locations"
          :aria-busy="isFetching ? 'true' : 'false'"
        >
          <template v-if="emptyState === 'no-container-types'">
            <UIEmptyState
              main="No container types yet"
              sub="Container types are added in Settings."
            />
            <div
              v-if="can('manage', 'ContainerTypes')"
              class="pb-4 text-center"
            >
              <router-link
                :to="{ name: 'SettingsContainerTypesView' }"
                class="text-sm font-medium text-gpx_red-200 hover:underline"
                @click="trackAddContainerTypesClick"
              >
                Add container types
              </router-link>
            </div>
          </template>
          <UIEmptyState
            v-else-if="emptyState === 'no-trackers'"
            main="No trackers of this type"
            sub="Set the container type on a tracker to find it here."
          />
          <div
            v-else-if="!hasSelection"
            class="px-4 py-6 text-sm text-gray-500"
          >
            Choose a container type and a destination.
          </div>
          <div
            v-else-if="isFetching && !sites.length"
            class="flex justify-center py-10"
            role="status"
            aria-label="Loading locations"
          >
            <ProgressSpinner
              :stroke-width="PVT_SPINNER_STROKE_WIDTH"
              :style="PVT_SPINNER_STYLE_MEDIUM"
              aria-hidden="true"
            />
          </div>
          <div v-else-if="isError" class="px-4 py-6 text-sm text-gray-500">
            Locations could not be loaded. Try again in a moment.
          </div>
          <UIEmptyState
            v-else-if="emptyState === 'none-within-radius'"
            :main="`No locations within ${radius} ${unitLabel}`"
            sub="Try a larger radius."
          />
          <UIEmptyState
            v-else-if="emptyState === 'none-at-locations'"
            main="No trackers of this type are at a location"
            sub="None of them is inside a location right now."
          />
          <FindContainersList
            v-else
            :sites="sortedSites"
            :unit="unit"
            :selected-geofence-id="selectedGeofenceId"
            @select="selectSite($event, 'list')"
          />
        </section>
      </div>

      <div
        class="min-h-[320px] flex-1 overflow-hidden rounded-xl border border-gpx_gray-800 bg-white shadow-sm md:min-h-0"
      >
        <FindContainersMap
          ref="map"
          :sites="sites"
          :trackers="trackers"
          :destination="destination"
          :search-radius-meters="radiusMeters"
          @select-site="selectSite($event, 'map')"
        />
      </div>
    </div>
  </div>
</template>
